import { ROUTES } from "@/constants/routeConstants";
import { useAppNavigate } from "@/hooks/useAppNavigate";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { Template } from "@/lib/types";
import { deleteStockTemplateById, getStockTemplates } from "@/sideEffects/api";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { selectActiveFirm } from "@/store/appSelectors";
import { useDispatch, useSelector } from "react-redux";
import { LoadingOverlay } from "@/components/LoadingOverlay";
import { AddRowButton } from "@/components/ax/dataTable/AddRowButton";
import { showApiError, showSuccess } from "@/lib/toast";
import { AxMediaViewer } from "@/components/ax/mediaGallery/AxMediaViewer";
import { setCurrentTemplate } from "@/store/templateEditor/templateEditorSlice";
import { useEffect, useMemo, useState } from "react";
import { PermissionChecker2 } from "@/components/permission/PermissionChecker2";
import { Button } from "@/components/ui/button";
import { Pencil, Trash } from "lucide-react";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { convertToTemplate } from "../template/utils/templateUtils";
import { TemplatePreview } from "../template/shared/TemplatePreview";
import _ from "lodash";
import { AppSelect } from "@/components/ui/app-select";

export const TemplateStockPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { goToTemplateStockAdd } = useAppNavigate();
  const [activeCategory, setActiveCategory] = useState<string>();

  useBreadcrumbs([{ path: ROUTES.TEMPLATE_STOCK, name: t("entities.templateStock") }]);

  useEffect(() => {
    dispatch(setCurrentTemplate(null));
  }, [dispatch]);

  const { data: stockTemplates } = useQuery({
    queryKey: ["getStockTemplates"],
    queryFn: () => getStockTemplates({ pageNumber: 1, numberOfObjectsPerPage: 99999 }),
  });

  const uniqTemplateCategories = useMemo(() => {
    return _.uniq(stockTemplates?.data?.result?.map((template) => template.category_name) || []);
  }, [stockTemplates]);

  return (
    <AxMediaViewer
      uniqQueryKey={"getStockTemplates"}
      fetcher={getStockTemplates}
      dynamicRequestParams={{ category_name: activeCategory }}
      className="axMediaStockTemplatesViewerGrid"
      itemKey="id"
      renderItem={(item: any) => <TemplateCard {...(convertToTemplate(item) as Template)} />}
      extraHeader={
        <>
          <AppSelect
            options={uniqTemplateCategories.map((category) => ({ label: category, value: category }))}
            placeholder={t("common.categoryName")}
            onChange={(value) => {
              setActiveCategory(value as string);
            }}
            value={activeCategory}
          />
          <AddRowButton
            title={t("common.addNewEntity", { entity: t("entities.templateStock") })}
            onClickAdd={goToTemplateStockAdd}
          />
        </>
      }
    />
  );
};

const TemplateCard: React.FC<Template> = ({ id, name, viewport_size, canvas_data, categoryName }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);
  const { goToTemplateStockEditor } = useAppNavigate();
  const queryClient = useQueryClient();

  const deleteTemplateMutation = useMutation({
    mutationFn: () => deleteStockTemplateById(String(activeFirm?.firmGuid), id),
    onSuccess: () => {
      showSuccess(t("common.deletedSuccess"));
      queryClient.invalidateQueries({ queryKey: ["getStockTemplates"] });
    },
    onError: showApiError,
  });

  return (
    <LoadingOverlay isLoading={deleteTemplateMutation.isPending}>
      <div className="group relative overflow-hidden bg-card rounded-lg border shadow-sm transition-all duration-200 hover:shadow-md hover:scale-[1.02]">
        <div className="cursor-pointer p-4" onClick={() => goToTemplateStockEditor(id)}>
          <div className="relative mb-4 py-6 overflow-hidden rounded-md border bg-muted/50">
            <TemplatePreview data={canvas_data} previewSize="small" />
          </div>
          <h3 className="font-semibold text-lg truncate">{name}</h3>
          <p className="text-sm text-muted-foreground">
            {categoryName} - {viewport_size}
          </p>
        </div>

        <PermissionChecker2 entity="DsStockTemplate" rights={["GetDetails"]}>
          <div className="bottom-0 left-0 right-0 flex justify-between items-center p-3 bg-muted/10 border-t">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 text-primary"
              onClick={() => goToTemplateStockEditor(id)}
            >
              <Pencil className="h-4 w-4 mr-1" />
              {t("common.edit")}
            </Button>
            <PermissionChecker2 entity="DsStockTemplate" rights={["Delete"]}>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="h-8 px-2 text-destructive">
                    <Trash className="h-4 w-4 mr-1" />
                    {t("common.deleteEntity.buttonText")}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t("common.deleteEntity.title")}</AlertDialogTitle>
                    <AlertDialogDescription>{t("common.deleteEntity.description")}</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                    <AlertDialogAction onClick={() => deleteTemplateMutation.mutate()}>
                      {t("common.deleteEntity.buttonText")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </PermissionChecker2>
          </div>
        </PermissionChecker2>
      </div>
    </LoadingOverlay>
  );
};
